// lib/services/compute/sticker_compute.ts

import Stage from "../lib/services/stage/stage";
import ShaderService from "../lib/services/shader/shader_service";
import ColorCameraPool from "../lib/services/capture/color_camera_pool";
import ColorCameraSlot from "../lib/services/capture/color_camera_slot";
import ColorGpuPool from "./color_gpu_pool";
import ColorGpuSlot from "./color_gpu_slot";
import GridInfo from "./gridinfo";
import StickerInfoGpu3x3 from "./stickerinfogpu3x3";
import { YuvFormat } from "../lib/services/compute/yuvdefs";
import StickerCpuSlot from "./sticker_cpu_slot";

// Pipelines
import ClearBlobsBasicPipeline from "./pipelines/clear_blobs_basic_pipeline";
import ClearBlobsPipeline from "./pipelines/clear_blobs_pipeline";
import ClearBlobTexPipeline from "./pipelines/clear_blob_tex_pipeline";
import BlackWhiteGridPipeline from "./pipelines/black_white_grid_pipeline";
import BlackWhiteGridReducePipeline from "./pipelines/black_white_grid_reduce_pipeline";
import BlackWhiteMaskAPipeline from "./pipelines/black_white_mask_a_pipeline";
import BlackWhiteMaskBPipeline from "./pipelines/black_white_mask_b_pipeline";
import Blob2RowPipeline from "./pipelines/blob2_row_pipeline";
import Blob2ColPipeline from "./pipelines/blob2_col_pipeline";
import ContrastPipeline from "./pipelines/contrast_pipeline";
import ComposeAPipeline from "./pipelines/compose_a_pipeline";
import ComposeBPipeline from "./pipelines/compose_b_pipeline";
import FilterNeighborsPipeline from "./pipelines/filter_neighbors_pipeline";
import FindNearestNeighbor3x3Pipeline from "./pipelines/find_nearest_neighbor_3x3_pipeline";
import DirAngleAndColorsPipeline from "./pipelines/dirangle_and_color_pipeline";
import LookForStickers3x3Pipeline from "./pipelines/look_for_stickers_3x3_pipeline";
import DebugBlobPipeline from "./pipelines/debug_blob_pipeline";
import PackBlobsPipeline from "./pipelines/pack_blobs_pipeline";
import PackStickerCodesPipeline from "./pipelines/pack_sticker_codes_pipeline";
import StickerCpuPool from "./sticker_cpu_pool";
import { VideoDisplayInfo } from "../scene/echodemo/video/video_display_helper";
import { rb_log, rb_debug_log, rb_throttle } from "../lib/services/compute/rb_log";

const BLACKWHITE_MASK_DIVS = 40;
const MAX_STICKERS = 256;
const MAX_BLOBS = 256;
const MASK_RES = 3;

export default class StickerCompute {
  private shader_service: ShaderService;
  private camera_pool: ColorCameraPool;
  gpu_pool: ColorGpuPool;
  cpu_pool: StickerCpuPool;

  private submitted_cpu_slots: StickerCpuSlot[] = [];
  private submitted_gpu_slots: ColorGpuSlot[] = [];

  private format: YuvFormat = "NV12";
  private width: number = 0;
  private height: number = 0;
  private size: number = 0;

  private grid_info: GridInfo | null = null;
  private sticker_info: StickerInfoGpu3x3;
  private sticker_buffer_size: number;
  private blob_buffer_size: number;

  private initialized: boolean = false;
  private enabled: boolean = false;
  private dispatch_counter: number = 0;

  // ---- Producer-side telemetry (logging only) ---------------------------
  // Per-second aggregate counters for the readback producer path. Reset each
  // reporting interval by log_producer_rates().
  private tel_attempts: number = 0;        // encode_readback calls with pending data
  private tel_no_idle_drops: number = 0;   // dropped: no Idle CPU slot available
  private tel_begin_record_fail: number = 0;
  private tel_publishes: number = 0;       // begin_map ok -> published
  private tel_cancels: number = 0;         // begin_map failed -> cancelled

  private clear_blobs_basic: ClearBlobsBasicPipeline | null = null;
  private clear_blobs: ClearBlobsPipeline | null = null;
  private clear_blob_tex: ClearBlobTexPipeline | null = null;
  //private bw_grid_reduce: BlackWhiteGridReducePipeline | null = null;
  private bw_grid: BlackWhiteGridPipeline | null = null;
  private bw_mask_a: BlackWhiteMaskAPipeline | null = null;
  private bw_mask_b: BlackWhiteMaskBPipeline | null = null;
  private blob2_row: Blob2RowPipeline | null = null;
  private blob2_col: Blob2ColPipeline | null = null;
  private contrast: ContrastPipeline | null = null;
  private compose_a: ComposeAPipeline | null = null;
  private compose_b: ComposeBPipeline | null = null;
  private filter_neighbors: FilterNeighborsPipeline | null = null;
  private find_nn: FindNearestNeighbor3x3Pipeline | null = null;
  private dir_angle: DirAngleAndColorsPipeline | null = null;
  private look_for_stickers: LookForStickers3x3Pipeline | null = null;
  private pack_sticker_codes: PackStickerCodesPipeline | null = null;

  //private debug_blob: DebugBlobPipeline | null = null;
  //private debug_basic: DebugBlobPipeline | null = null;
  private debug_dirangle: DebugBlobPipeline | null = null;
  private packed_blobs: PackBlobsPipeline | null = null;

  debug_mask_view: GPUTextureView | null = null;
  debug_blob_view: GPUTextureView | null = null;
  debug_width: number = 0;
  debug_height: number = 0;

  active_gpu_slot:ColorGpuSlot | null = null;

  private pending_readback_buffer: GPUBuffer | null = null;
  private pending_blob_readback_buffer: GPUBuffer | null = null;
  private pending_readback_sequence: number = 0;

  constructor(camera_pool: ColorCameraPool) {
    this.shader_service = Stage.instance().shader_service;
    this.camera_pool = camera_pool;
    this.gpu_pool = new ColorGpuPool();
    this.cpu_pool = new StickerCpuPool();
    this.sticker_info = new StickerInfoGpu3x3();
    this.sticker_buffer_size = this.sticker_info.get_buffer_size(MAX_STICKERS);
    this.blob_buffer_size = ColorGpuSlot.MAX_BLOBS * 288;
  }

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
  }

  is_enabled(): boolean {
    return this.enabled;
  }

  private initialize(device: GPUDevice, format: YuvFormat, width: number, height: number): void {
    if (this.initialized) return;

    this.format = format;
    this.width = width;
    this.height = height;
    this.size = width * height;

    this.grid_info = new GridInfo(width, height);

    const initial_data = this.grid_info.get_initial_data();
    const nngrid_size = this.grid_info.nn_grid_size;

    this.gpu_pool.initialize(
      device,
      "sticker_gpu",
      width,
      height,
      nngrid_size,
      this.sticker_buffer_size,
      this.blob_buffer_size
    );

    this.cpu_pool.initialize(device, "sticker_cpu", this.sticker_buffer_size,this.blob_buffer_size);

    for (let i = 0; i < 3; i++) {
      const slot = this.gpu_pool["slots"]?.[i];
      if (!slot?.info_buffer) continue;

      device.queue.writeBuffer(
        slot.info_buffer,
        0,
        initial_data.buffer,
        initial_data.byteOffset,
        initial_data.byteLength
      );
    }

    this.create_pipelines(device);
    this.initialized = true;
  }

  private rebuild(
    device: GPUDevice,
    format: YuvFormat,
    width: number,
    height: number
  ): void {
    this.destroy_pipelines();
    this.gpu_pool.shutdown();
    this.initialized = false;
    this.initialize(device, format, width, height);
  }

  private create_pipelines(device: GPUDevice): void {
    const get = (name: string) => this.shader_service.get(name);

    const s_cbb = get("clear_blobs_basic");
    if (s_cbb) this.clear_blobs_basic = new ClearBlobsBasicPipeline("clear_blobs_basic", s_cbb, device, this.size / 32);

    const s_cb = get("clear_blobs");
    if (s_cb) this.clear_blobs = new ClearBlobsPipeline("clear_blobs", s_cb, device, MAX_BLOBS / 32);

    const fmt = this.format;
    const nv = fmt === "NV12";

    const s_grid_key = nv ? "black_white_grid_nv12" : "black_white_grid_i420";
    const s_grid = get(s_grid_key);
    if (s_grid) this.bw_grid = new BlackWhiteGridPipeline(s_grid_key, s_grid, device, BLACKWHITE_MASK_DIVS * BLACKWHITE_MASK_DIVS, fmt);

    //const s_reduce = get("black_white_grid_reduce");
    //if (s_reduce) this.bw_grid_reduce = new BlackWhiteGridReducePipeline("black_white_grid_reduce", s_reduce, device);

    const s_ma_key = nv ? "black_white_mask_a_nv12" : "black_white_mask_a_i420";
    const s_ma = get(s_ma_key);
    if (s_ma) this.bw_mask_a = new BlackWhiteMaskAPipeline(s_ma_key, s_ma, device, this.size / (MASK_RES * MASK_RES), fmt);

    const s_mb_key = nv ? "black_white_mask_b_nv12" : "black_white_mask_b_i420";
    const s_mb = get(s_mb_key);
    if (s_mb) this.bw_mask_b = new BlackWhiteMaskBPipeline(s_mb_key, s_mb, device, this.size / (MASK_RES * MASK_RES), fmt);

    const s_row = get("fillblobsmapx_bw");
    if (s_row) this.blob2_row = new Blob2RowPipeline("fillblobsmapx_bw", s_row, device, this.height);

    const s_col = get("fillblobsmapy_bw");
    if (s_col) this.blob2_col = new Blob2ColPipeline("fillblobsmapy_bw", s_col, device, this.width);

    const s_con_key = nv ? "filterblobscontrast_nv12" : "filterblobscontrast_i420";
    const s_con = get(s_con_key);
    if (s_con) this.contrast = new ContrastPipeline(s_con_key, s_con, device, this.height, fmt);

    const s_ca = get("composebloblista");
    if (s_ca) this.compose_a = new ComposeAPipeline("composebloblista", s_ca, device, this.height);

    const s_cb2 = get("composebloblistb");
    if (s_cb2) this.compose_b = new ComposeBPipeline("composebloblistb", s_cb2, device, 1);

    const s_fn = get("filterblobsneighbors");
    if (s_fn) this.filter_neighbors = new FilterNeighborsPipeline("filterblobsneighbors", s_fn, device, MAX_BLOBS);

    const s_nn = get("findnearestneighbor3x3");
    if (s_nn) this.find_nn = new FindNearestNeighbor3x3Pipeline("findnearestneighbor3x3", s_nn, device, MAX_BLOBS);

    const s_da_key = nv ? "get_dirangle_and_colors_nv12" : "get_dirangle_and_colors_i420";
    const s_da = get(s_da_key);
    if (s_da) this.dir_angle = new DirAngleAndColorsPipeline(s_da_key, s_da, device, MAX_BLOBS, fmt);

    const s_lfs_key = "lookforstickersall";
    const s_lfs = get(s_lfs_key);
    if (s_lfs) this.look_for_stickers = new LookForStickers3x3Pipeline(s_lfs_key, s_lfs, device, MAX_STICKERS);

    const s_psc_key = "pack_sticker_codes";
    const s_psc = get(s_psc_key);
    if (s_psc) this.pack_sticker_codes = new PackStickerCodesPipeline(s_psc_key, s_psc, device, MAX_STICKERS);

    const s_cbt = get("clearblobtex");
    if (s_cbt) this.clear_blob_tex = new ClearBlobTexPipeline("clearblobtex", s_cbt, device, this.size);

   /* const db_b_key = "debug_blob";
    const db_b = get(db_b_key);
    if (db_b) this.debug_blob = new DebugBlobPipeline(db_b_key, db_b, device, MAX_BLOBS);

    const db_basic_key = "debug_blob_basic";
    const db_basic = get(db_basic_key);
    if (db_basic) this.debug_basic = new DebugBlobPipeline(db_basic_key, db_basic, device, this.size);

     const db_circletype_key = "draw_circletype_blobs";
    const db_circletype = get(db_circletype_key);
    if (db_circletype) this.draw_circletype_blobs = new DebugBlobPipeline(db_circletype_key, db_circletype, device, MAX_BLOBS);
*/
    const db_dirangle_key = "debug_blob_dirangle";
    const db_dirangle = get(db_dirangle_key);
    if (db_dirangle) this.debug_dirangle = new DebugBlobPipeline(db_dirangle_key, db_dirangle, device, MAX_BLOBS);

    const pb_key = "pack_blobs";
    const pb = get(pb_key);
    if (pb) this.packed_blobs = new PackBlobsPipeline(pb_key, pb, device);
 
  }

  private destroy_pipelines(): void {
    this.clear_blobs_basic = null;
    this.clear_blobs = null;
    this.bw_grid = null;
    //this.bw_grid_reduce = null;
    this.bw_mask_a = null;
    this.bw_mask_b = null;
    this.blob2_row = null;
    this.blob2_col = null;
    this.contrast = null;
    this.compose_a = null;
    this.compose_b = null;
    this.filter_neighbors = null;
    this.find_nn = null;
    this.dir_angle = null;
    this.look_for_stickers = null;
    this.pack_sticker_codes = null;

    this.clear_blob_tex = null;
    //this.debug_blob = null;
    //this.debug_basic = null;
    this.debug_dirangle = null;
    this.packed_blobs = null;
  }

  private build_fixed_bindings(slot: ColorGpuSlot): void {
    this.clear_blobs_basic?.build(slot.info_buffer!, slot.blob_basic_buffer!);
    this.clear_blobs?.build(slot.blob2_buffer!);

    this.compose_a?.build(slot.info_buffer!, slot.blob_basic_buffer!, slot.blob2_collect_buffer!);
    this.compose_b?.build(slot.info_buffer!, slot.blob2_collect_buffer!, slot.blob2_buffer!);
    this.filter_neighbors?.build(slot.info_buffer!, slot.blob_basic_buffer!, slot.blob2_buffer!);
    this.find_nn?.build(slot.info_buffer!, slot.blob2_buffer!, slot.nngrid_buffer!);

    this.bw_grid?.build(slot.info_buffer!, slot.bwgrid_buffer!);
    //this.bw_grid_reduce?.build(slot.scene_cbcr_buffer!, slot.scene_cbcr_avg_buffer!);

    this.bw_mask_a?.build(slot.info_buffer!, slot.bwgrid_buffer!);
    this.bw_mask_b?.build(slot.info_buffer!, slot.bwgrid_buffer!);
    this.blob2_row?.build(slot.info_buffer!, slot.blob_basic_buffer!);
    this.blob2_col?.build(slot.info_buffer!, slot.blob_basic_buffer!);
    this.contrast?.build(slot.info_buffer!, slot.blob_basic_buffer!);
    this.dir_angle?.build(slot.blob2_buffer!);
    this.look_for_stickers?.build(slot.info_buffer!, slot.blob2_buffer!, slot.sticker_buffer!);
    this.pack_sticker_codes?.build(slot.sticker_buffer!);

    this.clear_blob_tex?.build(slot.info_buffer!);
    this.debug_dirangle?.build(slot.info_buffer!, slot.blob_basic_buffer!, slot.blob2_buffer!);
    this.packed_blobs?.build(slot.blob2_buffer!, slot.display_info_buffer!, 
                             slot.packed_count_buffer!,slot.packed_blobs_buffer!);
  }

  private rebuild_texture_bindings(cam_slot: ColorCameraSlot, gpu_slot: ColorGpuSlot): void {
    const y = cam_slot.y_view!;
    const mask = gpu_slot.black_white_mask_texture_view!;
    const mask_read = gpu_slot.black_white_mask_texture_view_read!;
    const blob_debug = gpu_slot.blob_debug_texture_view!;

    if (cam_slot.format === "NV12") {
      const cbcr = cam_slot.cbcr_view!;
      this.bw_grid?.rebuild(y, cbcr);
      this.bw_mask_a?.rebuild(y, cbcr, mask, mask_read);
      this.bw_mask_b?.rebuild(y, cbcr, mask, mask_read);
      this.contrast?.rebuild(y, cbcr, mask);
      this.dir_angle?.rebuild(y, cbcr);
    } else {
      const u = cam_slot.u_view!;
      const v = cam_slot.v_view!;
      this.bw_grid?.rebuild(y, u, v);
      this.bw_mask_a?.rebuild(y, u, mask, mask_read, v);
      this.bw_mask_b?.rebuild(y, u, mask, mask_read, v);
      this.contrast?.rebuild(y, u, mask, v);
      this.dir_angle?.rebuild(y, u, v);
    }

    this.blob2_row?.rebuild(mask);
    this.blob2_col?.rebuild(mask);
    this.clear_blob_tex?.rebuild(blob_debug);
    this.debug_dirangle?.rebuild(blob_debug);
    //this.draw_circletype_blobs?.rebuild(blob_debug);
  }

  private write_info_buffer(device: GPUDevice, slot: ColorGpuSlot): void {
    if (!this.grid_info || !slot.info_buffer) return;
    const data = this.grid_info.get_initial_data();
    device.queue.writeBuffer(
      slot.info_buffer,
      0,
      data.buffer,
      data.byteOffset,
      data.byteLength
    );

  }

  private write_video_info_buffer(device: GPUDevice, slot: ColorGpuSlot,info:VideoDisplayInfo): void {
    if (!slot.display_info_buffer) return;

     const data = new Int32Array(8);
     data[0] = info.mode;
     data[1] = info.srcW;
     data[2] = info.srcH;
     data[3] = info.dstW;
     data[4] = info.dstH;
     data[5]= 0; //padding
     data[5]= 0;
     data[7]= 0;
     
    device.queue.writeBuffer(
      slot.display_info_buffer,
      0,
      data.buffer,
      data.byteOffset,
      data.byteLength
    );

  }

  dispatch_frame(device: GPUDevice, pass: GPUComputePassEncoder, encoder: GPUCommandEncoder,info:VideoDisplayInfo): boolean {
    if (!this.enabled) {
      rb_debug_log("[sticker] disabled");
      return false;
    }

    const cam_slot = this.camera_pool.peek_latest_ready_slot();
    if (!cam_slot) {
      rb_debug_log("[sticker] no cam slot");
      return false;
    }
    if (!cam_slot.y_view) {
      rb_debug_log("[sticker] no y_view");
      return false;
    }

    if (!this.initialized) {
      this.initialize(device, cam_slot.format as YuvFormat, cam_slot.y_width, cam_slot.y_height);
    }

    // Rebuild when EITHER dimensions OR the YUV format changed. A format
    // change (now possible per-frame) swaps every _nv12/_i420 shader variant
    // and the chroma texture bindings; rebuilding on dims alone would leave
    // stale-format pipelines bound against new-format textures.
    const cam_format = cam_slot.format as YuvFormat;
    if (
      cam_slot.y_width !== this.width ||
      cam_slot.y_height !== this.height ||
      cam_format !== this.format
    ) {
      this.rebuild(device, cam_format, cam_slot.y_width, cam_slot.y_height);
    }

    const gpu_slot = this.gpu_pool.acquire_idle_slot();
    if (!gpu_slot) {
      rb_debug_log("[sticker] no gpu slot, idle:", this.gpu_pool.get_idle_count?.() ?? this.gpu_pool.get_idle_count?.());
      return false;
    }

    this.active_gpu_slot = gpu_slot;
    
    gpu_slot.begin_record();

    this.dispatch_counter++;

    this.write_info_buffer(device, gpu_slot);
    this.write_video_info_buffer(device,gpu_slot,info);
    this.build_fixed_bindings(gpu_slot);
    this.rebuild_texture_bindings(cam_slot, gpu_slot);

    this.clear_blobs_basic?.dispatch(pass);
    this.clear_blobs?.dispatch(pass);
    this.bw_grid?.dispatch(pass);
    //this.bw_grid_reduce?.dispatch(pass);
    this.bw_mask_a?.dispatch(pass);
    this.bw_mask_b?.dispatch(pass);
    this.blob2_row?.dispatch(pass);
    this.blob2_col?.dispatch(pass);
    //do we even need this now?
    //this.contrast?.dispatch(pass);
    this.compose_a?.dispatch(pass);
    this.compose_b?.dispatch(pass);
    this.filter_neighbors?.dispatch(pass);
    this.find_nn?.dispatch(pass);
    this.dir_angle?.dispatch(pass);
    this.look_for_stickers?.dispatch(pass);
    this.pack_sticker_codes?.dispatch(pass);

    this.clear_blob_tex?.dispatch(pass);
   // should be depnding on if showing blobs or color
    this.debug_dirangle?.dispatch(pass);
    this.packed_blobs?.dispatch(pass);
    this.pending_readback_buffer = gpu_slot.sticker_buffer;
    this.pending_blob_readback_buffer = gpu_slot.blob2_buffer;
    this.pending_readback_sequence = this.dispatch_counter;

    this.submitted_gpu_slots.push(gpu_slot);

    this.debug_mask_view = gpu_slot.black_white_mask_texture_view;
    this.debug_blob_view = gpu_slot.blob_debug_texture_view;
    this.debug_width = gpu_slot.width;
    this.debug_height = gpu_slot.height;

    return true;
  }

  encode_readback(encoder: GPUCommandEncoder): void {
    if (!this.pending_readback_buffer || !this.pending_blob_readback_buffer) return;

    this.tel_attempts++;

    const cpu_slot = this.cpu_pool.acquire_idle_slot();

    if (rb_throttle("cpu_census")) {
      rb_log("cpu_census", {
        at: "encode_readback",
        acquired: !!cpu_slot,
        pending_seq: this.pending_readback_sequence,
        ...this.cpu_pool.census(),
      });
    }

    this.log_producer_rates();

    if (!cpu_slot) {
      this.tel_no_idle_drops++;
      this.pending_readback_buffer = null;
      this.pending_blob_readback_buffer = null;
      this.pending_readback_sequence = 0;
      return;
    }

    if (!cpu_slot.begin_record()) {
      this.tel_begin_record_fail++;
      console.log("[sticker] encode_readback: begin_record failed");
      this.pending_readback_buffer = null;
      this.pending_blob_readback_buffer = null;
      this.pending_readback_sequence = 0;
      return;
    }

    cpu_slot.encode_copy(encoder, this.pending_readback_buffer, this.pending_blob_readback_buffer,this.pending_readback_sequence);
    this.submitted_cpu_slots.push(cpu_slot);

    this.pending_readback_buffer = null;
    this.pending_blob_readback_buffer = null;
    this.pending_readback_sequence = 0;
  }

  /**
   * Logging only: once/sec, emit producer-path aggregate counters then reset
   * them. publishes/cancels are incremented asynchronously in after_submit's
   * onSubmittedWorkDone continuation.
   */
  private log_producer_rates(): void {
    if (!rb_throttle("rb_rates")) return;

    rb_log("rb_rates", {
      attempts: this.tel_attempts,
      no_idle_drops: this.tel_no_idle_drops,
      begin_record_fail: this.tel_begin_record_fail,
      publishes: this.tel_publishes,
      cancels: this.tel_cancels,
      latest_published_seq: this.cpu_pool.get_latest_published_sequence(),
    });

    this.tel_attempts = 0;
    this.tel_no_idle_drops = 0;
    this.tel_begin_record_fail = 0;
    this.tel_publishes = 0;
    this.tel_cancels = 0;
  }

  after_submit(device: GPUDevice): void {
    if (this.submitted_cpu_slots.length === 0 && this.submitted_gpu_slots.length === 0) return;

    const cpu_batch = this.submitted_cpu_slots;
    this.submitted_cpu_slots = [];

    const gpu_batch = this.submitted_gpu_slots;
    this.submitted_gpu_slots = [];

    const t_submit = performance.now();
    const batch_seq = cpu_batch.length > 0
      ? cpu_batch[cpu_batch.length - 1].source_gpu_sequence
      : 0;

    device.queue.onSubmittedWorkDone()
      .then(async () => {
        if (rb_throttle("workdone_timing")) {
          rb_log("workdone", {
            elapsed_ms: Math.round((performance.now() - t_submit) * 100) / 100,
            cpu_batch: cpu_batch.length,
            gpu_batch: gpu_batch.length,
            seq: batch_seq,
          });
        }

        for (const gpu_slot of gpu_batch) {
          gpu_slot.recycle();
        }

        for (const cpu_slot of cpu_batch) {
          const result = await cpu_slot.begin_map();
          if (result.ok) {
            this.tel_publishes++;
            this.cpu_pool.publish_slot(cpu_slot, performance.now() / 1000);
          } else if (result.cancelled) {
            // Deferred cancellation (clear) or stale generation: begin_map has
            // already cleaned up the slot it owns. Return it to the free-list
            // only if this operation still owns it — never recycle a slot a
            // newer operation has taken.
            this.tel_cancels++;
            this.cpu_pool.finalize_cancelled_slot(cpu_slot, result.token);
          } else {
            // Genuine map failure (already logged by begin_map).
            this.tel_cancels++;
            this.cpu_pool.cancel_slot(cpu_slot);
          }
        }
      })
      .catch((err) => {
        console.error("[sticker] readback failed", err);

        for (const gpu_slot of gpu_batch) {
          gpu_slot.recycle();
        }

        for (const cpu_slot of cpu_batch) {
          this.cpu_pool.cancel_slot(cpu_slot);
        }
      });
  }

  shutdown(): void {
    this.destroy_pipelines();
    this.gpu_pool.shutdown();
    this.initialized = false;
    this.enabled = false;
    this.submitted_cpu_slots = [];
    this.submitted_gpu_slots = [];
    this.pending_readback_buffer = null;
    this.pending_readback_sequence = 0;
  }
}
